import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Wallet, Clock3, Landmark, Vault, Loader2, HandCoins, Users2, TriangleAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ReportDialog from "@/components/ReportDialog";
import { StatCard, StatusBadge } from "@/components/Ui";
import ConsoleHeader from "@/components/ConsoleHeader";
import TransactionLedger from "@/components/TransactionLedger";
import CashLedger from "@/components/CashLedger";
import { api, errMsg } from "@/lib/api";
import { money, fmtDateTime, statusStyle } from "@/lib/format";

function ShiftRow({ s, action }) {
  return (
    <div className="card-soft flex flex-col gap-3 p-4 md:flex-row md:items-center" data-testid={`cashier-shift-row-${s.id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2"><b>{s.teller_name}</b><span className="font-mono text-xs text-slate-500">{s.number}</span><StatusBadge status={s.status} cls={statusStyle[s.status]} /></div>
        <div className="text-xs text-slate-500">{s.station_name} · {fmtDateTime(s.opened_at)} → {s.closed_at ? fmtDateTime(s.closed_at) : "still open"} · {s.tickets} tickets</div>
      </div>
      <div className="grid grid-cols-3 gap-4 text-right text-xs">
        <div><div className="label-xs">Cash</div><b className="text-sm">{money(s.cash_total)}</b></div>
        <div><div className="label-xs">MoMo</div><b className="text-sm">{money(s.momo_total)}</b></div>
        <div><div className="label-xs">{s.status === "received" ? "Variance" : "Expected"}</div><b className={`text-sm ${s.status === "received" && s.variance < 0 ? "text-rose-600" : "text-[#0052FF]"}`}>{money(s.status === "received" ? s.variance : s.expected_cash)}</b></div>
      </div>
      {action}
    </div>
  );
}

function ReceiveDialog({ shift, onClose, onDone }) {
  const [cash, setCash] = useState("");
  const [notes, setNotes] = useState("");
  useEffect(() => { if (shift) { setCash(String(shift.declared_cash ?? shift.expected_cash)); setNotes(""); } }, [shift]);
  if (!shift) return null;
  const diff = Number(cash || 0) - shift.expected_cash;
  const submit = async () => {
    try { const { data } = await api.post(`/cashier/shifts/${shift.id}/receive`, { counted_cash: Number(cash), notes }); toast.success("Cash received - receipt ready"); onDone(data); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-3xl sm:max-w-md" data-testid="receive-dialog">
        <DialogHeader><DialogTitle className="font-display">Receive cash from {shift.teller_name}</DialogTitle></DialogHeader>
        <div className="space-y-1.5 rounded-2xl bg-slate-50 p-4 text-sm">
          <div className="flex justify-between"><span>System expected cash</span><b>{money(shift.expected_cash)}</b></div>
          <div className="flex justify-between"><span>Teller declared</span><b>{money(shift.declared_cash)}</b></div>
        </div>
        <Label className="text-xs font-semibold text-slate-600">Cash counted by you</Label>
        <Input value={cash} onChange={(e) => setCash(e.target.value.replace(/[^\d.]/g, ""))} className="h-12 text-lg font-bold" data-testid="counted-cash-input" />
        <div className={`rounded-xl p-2.5 text-sm font-bold ${Math.abs(diff) < 0.01 ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`} data-testid="receive-variance">{Math.abs(diff) < 0.01 ? "Balanced - no variance" : diff > 0 ? `Over by ${money(diff)}` : `Short by ${money(-diff)}`}</div>
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (optional)" data-testid="receive-notes-input" />
        <Button disabled={cash === ""} onClick={submit} className="h-12 rounded-xl bg-[#0052FF] font-bold hover:bg-[#0041CC]" data-testid="confirm-receive-button">Confirm & print receipt</Button>
      </DialogContent>
    </Dialog>
  );
}

function Deposits({ onChanged }) {
  const [list, setList] = useState([]);
  const [f, setF] = useState({ amount: "", bank: "", account: "", reference: "", notes: "" });
  const load = () => api.get("/cashier/deposits").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const save = async (e) => {
    e.preventDefault();
    try { await api.post("/cashier/deposits", { ...f, amount: Number(f.amount) }); toast.success("Bank deposit recorded"); setF({ amount: "", bank: "", account: "", reference: "", notes: "" }); load(); onChanged(); } catch (er) { toast.error(errMsg(er)); }
  };
  return (
    <div className="grid gap-4 lg:grid-cols-[340px_1fr]">
      <form onSubmit={save} className="card-soft space-y-3 p-5" data-testid="deposit-form">
        <div className="font-display font-bold">Record bank deposit</div>
        {[["amount", "Amount (GH₵)"], ["bank", "Bank"], ["account", "Account no."], ["reference", "Deposit slip / reference"]].map(([k, l]) => (
          <Input key={k} placeholder={l} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`deposit-${k}-input`} />
        ))}
        <Textarea placeholder="Notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
        <Button type="submit" className="w-full rounded-xl bg-[#0052FF] font-bold hover:bg-[#0041CC]" data-testid="deposit-save-button">Save deposit</Button>
      </form>
      <div className="card-soft divide-y divide-slate-100">
        {list.map((d) => (
          <div key={d.id} className="flex items-center justify-between p-4 text-sm" data-testid={`deposit-row-${d.id}`}>
            <div><b>{d.bank}</b> <span className="text-slate-500">{d.account} · Ref {d.reference}</span><div className="text-xs text-slate-500">{fmtDateTime(d.created_at)} · {d.cashier_name}</div></div>
            <b>{money(d.amount)}</b>
          </div>
        ))}
        {!list.length && <p className="p-6 text-center text-sm text-slate-500">No deposits recorded.</p>}
      </div>
    </div>
  );
}

function BuffersTab({ onChanged }) {
  const [list, setList] = useState([]);
  const [give, setGive] = useState(null);
  const [amount, setAmount] = useState("");
  const load = () => api.get("/cashier/buffers").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const open = (b) => { setGive(b); setAmount(String(b.to_give || "")); };
  const submit = async () => {
    try { const { data } = await api.post("/cashier/buffer-topup", { station_id: give.station_id, amount: Number(amount) }); toast.success(`Buffer sent to station master · receipt ${data.receipt_no}`); setGive(null); load(); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="card-soft divide-y divide-slate-100" data-testid="cashier-buffers">
      {list.map((b) => (
        <div key={b.station_id} className={`flex flex-col gap-3 p-4 md:flex-row md:items-center ${b.low ? "bg-rose-50/50" : ""}`} data-testid={`buffer-row-${b.station_id}`}>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2"><b>{b.city} — {b.station_name}</b>{b.low && <span className="chip bg-rose-100 text-rose-700" data-testid={`buffer-low-${b.station_id}`}><TriangleAlert className="mr-1 h-3 w-3" />Low (&lt;5%)</span>}</div>
            <div className="text-xs text-slate-500">Limit {money(b.buffer_amount)} · to top up {money(b.to_give)}</div>
          </div>
          <div className="grid grid-cols-4 gap-4 text-right text-xs">
            <div><div className="label-xs">Opening</div><b className="text-sm">{money(b.opening_balance)}</b></div>
            <div><div className="label-xs">Recd today</div><b className="text-sm text-sky-600">{money(b.today_received)}</b></div>
            <div><div className="label-xs">To buses</div><b className="text-sm text-amber-600">{money(b.today_given)}</b></div>
            <div><div className="label-xs">Balance</div><b className={`text-sm ${b.low ? "text-rose-600" : "text-[#0052FF]"}`}>{money(b.balance)}</b></div>
          </div>
          <Button className="rounded-full bg-[#0052FF] hover:bg-[#0041CC]" disabled={b.buffer_amount <= 0} onClick={() => open(b)} data-testid={`give-buffer-${b.station_id}`}><HandCoins className="h-4 w-4" />Give buffer</Button>
        </div>
      ))}
      {!list.length && <p className="p-6 text-center text-sm text-slate-500">No active stations.</p>}
      <Dialog open={!!give} onOpenChange={(o) => !o && setGive(null)}>
        <DialogContent className="rounded-3xl sm:max-w-sm" data-testid="give-buffer-dialog">
          <DialogHeader><DialogTitle className="font-display">Give buffer to {give?.station_name}</DialogTitle></DialogHeader>
          <div className="space-y-1.5 rounded-2xl bg-slate-50 p-4 text-sm">
            <div className="flex justify-between"><span>Buffer limit</span><b>{money(give?.buffer_amount)}</b></div>
            <div className="flex justify-between"><span>Currently in hand</span><b>{money(give?.in_hand)}</b></div>
          </div>
          <Label className="text-xs font-semibold text-slate-600">Amount to hand over (GH₵)</Label>
          <Input value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ""))} className="h-12 text-lg font-bold" data-testid="buffer-amount-input" />
          <Button disabled={!amount} onClick={submit} className="h-12 rounded-xl bg-[#0052FF] font-bold hover:bg-[#0041CC]" data-testid="confirm-buffer-button">Send &amp; issue receipt</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Withdrawals({ onChanged }) {
  const [list, setList] = useState([]);
  const [f, setF] = useState({ amount: "", bank: "", reference: "", notes: "" });
  const load = () => api.get("/cashier/withdrawals").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const save = async (e) => {
    e.preventDefault();
    try { await api.post("/cashier/withdrawals", { ...f, amount: Number(f.amount) }); toast.success("Bank withdrawal recorded"); setF({ amount: "", bank: "", reference: "", notes: "" }); load(); onChanged(); } catch (er) { toast.error(errMsg(er)); }
  };
  return (
    <div className="grid gap-4 lg:grid-cols-[340px_1fr]">
      <form onSubmit={save} className="card-soft space-y-3 p-5" data-testid="withdrawal-form">
        <div className="font-display font-bold">Record bank withdrawal (cash into vault)</div>
        {[["amount", "Amount (GH₵)"], ["bank", "Bank"], ["reference", "Withdrawal slip / reference"]].map(([k, l]) => (
          <Input key={k} placeholder={l} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`withdrawal-${k}-input`} />
        ))}
        <Textarea placeholder="Notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
        <Button type="submit" className="w-full rounded-xl bg-[#0052FF] font-bold hover:bg-[#0041CC]" data-testid="withdrawal-save-button">Save withdrawal</Button>
      </form>
      <div className="card-soft divide-y divide-slate-100">
        {list.map((d) => (
          <div key={d.id} className="flex items-center justify-between p-4 text-sm" data-testid={`withdrawal-row-${d.id}`}>
            <div><b>{d.bank}</b> <span className="text-slate-500">Ref {d.reference}</span><div className="text-xs text-slate-500">{fmtDateTime(d.created_at)} · {d.cashier_name}</div></div>
            <b className="text-emerald-600">+ {money(d.amount)}</b>
          </div>
        ))}
        {!list.length && <p className="p-6 text-center text-sm text-slate-500">No withdrawals recorded.</p>}
      </div>
    </div>
  );
}

export default function CashierPage() {
  const [sum, setSum] = useState(null);
  const [lists, setLists] = useState({ closed: [], received: [], open: [] });
  const [receiving, setReceiving] = useState(null);
  const [report, setReport] = useState(null);
  const load = useCallback(async () => {
    const [s, c, r, o] = await Promise.all([api.get("/cashier/summary"), ...["closed", "received", "open"].map((st) => api.get("/cashier/shifts", { params: { status: st } }))]);
    setSum(s.data);
    setLists({ closed: c.data, received: r.data, open: o.data });
  }, []);
  useEffect(() => { load(); }, [load]);
  const view = async (id) => { const { data } = await api.get(`/shifts/${id}/report`); setReport(data); };

  if (!sum) return <div className="grid h-64 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-[#0052FF]" /></div>;
  return (
    <div className="mx-auto max-w-7xl space-y-5 px-4 py-6 sm:px-6" data-testid="cashier-page">
      <ConsoleHeader role="cashier" icon={Wallet} title="Shift receipts, vault & bank" subtitle="Receive shifts, run the vault, bank deposits and station buffers." />
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-6">
        <StatCard label="Awaiting handover" value={sum.pending_count} sub={money(sum.pending_expected)} icon={Clock3} tint="bg-amber-50 text-amber-600" testid="cashier-pending" />
        <StatCard label="Received today" value={money(sum.received_today)} icon={HandCoins} tint="bg-emerald-50 text-emerald-600" testid="cashier-received-today" />
        <StatCard label="Banked today" value={money(sum.deposited_today)} icon={Landmark} testid="cashier-deposited-today" />
        <StatCard label="Cash in vault" value={money(sum.vault_balance)} sub={`${sum.open_shifts} teller${sum.open_shifts === 1 ? "" : "s"} on duty`} icon={Vault} tint="bg-sky-50 text-sky-600" testid="cashier-vault" />
        <StatCard label="With station masters" value={money(sum.with_station_masters)} icon={Users2} tint="bg-indigo-50 text-indigo-600" testid="cashier-with-sm" />
        <StatCard label="Buffer to give" value={money(sum.buffer_to_give)} sub={sum.buffer_to_give > 0 ? (sum.low_buffer_stations ? `${sum.low_buffer_stations} low` : "to top up") : "all topped up"} icon={sum.low_buffer_stations ? TriangleAlert : HandCoins} tint={sum.low_buffer_stations ? "bg-rose-50 text-rose-600" : "bg-slate-100 text-slate-600"} testid="cashier-buffer-to-give" />
      </div>
      <Tabs defaultValue="pending">
        <TabsList className="flex w-full max-w-full justify-start gap-1 overflow-x-auto rounded-xl no-scrollbar">
          <TabsTrigger value="pending" data-testid="cashier-tab-pending">Pending ({lists.closed.length})</TabsTrigger>
          <TabsTrigger value="received" data-testid="cashier-tab-received">Received</TabsTrigger>
          <TabsTrigger value="open" data-testid="cashier-tab-open">On duty</TabsTrigger>
          <TabsTrigger value="buffers" data-testid="cashier-tab-buffers">Station buffers</TabsTrigger>
          <TabsTrigger value="deposits" data-testid="cashier-tab-deposits">Bank deposits</TabsTrigger>
          <TabsTrigger value="withdrawals" data-testid="cashier-tab-withdrawals">Withdrawals</TabsTrigger>
          <TabsTrigger value="transactions" data-testid="cashier-tab-transactions">Cash acks</TabsTrigger>
          <TabsTrigger value="ledger" data-testid="cashier-tab-ledger">Ledger</TabsTrigger>
        </TabsList>
        <TabsContent value="pending" className="space-y-2">
          {lists.closed.map((s) => <ShiftRow key={s.id} s={s} action={<Button className="rounded-full bg-[#0052FF] hover:bg-[#0041CC]" onClick={() => setReceiving(s)} data-testid={`receive-button-${s.id}`}><Wallet className="h-4 w-4" />Receive</Button>} />)}
          {!lists.closed.length && <p className="card-soft p-6 text-center text-sm text-slate-500">No shifts waiting for handover.</p>}
        </TabsContent>
        <TabsContent value="received" className="space-y-2">
          {lists.received.map((s) => <ShiftRow key={s.id} s={s} action={<Button variant="outline" className="rounded-full" onClick={() => view(s.id)} data-testid={`receipt-button-${s.id}`}>Receipt</Button>} />)}
        </TabsContent>
        <TabsContent value="open" className="space-y-2">
          {lists.open.map((s) => <ShiftRow key={s.id} s={s} action={<Button variant="outline" className="rounded-full" onClick={() => view(s.id)} data-testid={`live-report-button-${s.id}`}>Live report</Button>} />)}
          {!lists.open.length && <p className="card-soft p-6 text-center text-sm text-slate-500">No tellers on duty.</p>}
        </TabsContent>
        <TabsContent value="deposits"><Deposits onChanged={load} /></TabsContent>
        <TabsContent value="buffers"><BuffersTab onChanged={load} /></TabsContent>
        <TabsContent value="withdrawals"><Withdrawals onChanged={load} /></TabsContent>
        <TabsContent value="transactions"><TransactionLedger /></TabsContent>
        <TabsContent value="ledger"><CashLedger /></TabsContent>
      </Tabs>
      <ReceiveDialog shift={receiving} onClose={() => setReceiving(null)} onDone={(r) => { setReceiving(null); setReport(r); load(); }} />
      <ReportDialog report={report} onClose={() => setReport(null)} title="Cash handover receipt" />
    </div>
  );
}
